import type { FC } from 'react'
import React, { useState, useEffect } from 'react'
import type { Theme } from '@material-ui/core/styles'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import type { FormikProps } from 'formik'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useDispatch } from 'react-redux'
import { useHistory, Link } from 'react-router-dom'

import { Typography, Button, TextField } from '@homeviews/shared-ui'
import { useLoginMutation, setToken } from '@homeviews/store'
import { mixpanelTrack } from '@homeviews/seo'

import { setAccessToken } from '../../utils'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    separator: {},
    formFooter: {
      marginBottom: theme.spacing(2.5),
    },
    checkboxWrapper: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: theme.spacing(-1.2),
    },
    resetPasswordLink: {
      color: '#00837D !important',
    },
  }),
)

interface IProps {
  withSocialAuth?: boolean
}

interface ILoginForm {
  email: string
  password: string
}

const LoginForm: FC<IProps> = ({ withSocialAuth = false }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const [login] = useLoginMutation()
  const classes = useStyles()

  const [loading, setLoading] = useState<boolean>(false)
  const [formError, setFormError] = useState<string>('')

  useEffect(() => {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)

    const token = urlParams.get('token')
    if (!token) return

    setAccessToken(token)
    dispatch(setToken({ token }))

    history.push('/')
  }, [dispatch, history])

  const onSubmit = async (values: ILoginForm) => {
    setLoading(true)

    const payload = {
      username: values.email,
      password: values.password,
    }

    try {
      const authData = await login(payload).unwrap()

      localStorage.setItem('homeviews_access_token', authData?.access_token)

      dispatch(setToken({ token: authData?.access_token }))

      mixpanelTrack('LOGIN')

      history.push('/')
    } catch (err) {
      console.log(err)
      setFormError('Invalid credentials. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={classes.root}>
      {!!withSocialAuth && (
        <div className={classes.separator}>
          <span />
          <Typography>Or use your email and password</Typography>
          <span />
        </div>
      )}
      <Formik
        initialValues={{
          email: '',
          password: '',
        }}
        onSubmit={onSubmit}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email('Enter valid email')
            .required('Email field is required'),
          password: Yup.string().required('Password field is required'),
        })}
      >
        {(props: FormikProps<ILoginForm>) => {
          const { values, touched, errors, handleBlur, handleChange } = props

          return (
            <Form>
              <TextField
                label="Email address"
                value={values.email}
                data-id="t-email"
                id="email"
                error={errors.email && touched.email ? true : false}
                helperText={errors.email}
                onChange={(e) => {
                  handleChange(e)
                  setFormError('')
                }}
                onBlur={handleBlur}
                fullWidth
              />
              <TextField
                label="Password"
                value={values.password}
                data-id="t-password"
                id="password"
                type="password"
                autoComplete="current-password"
                error={errors.password && touched.password ? true : false}
                helperText={errors.password}
                onChange={(e) => {
                  handleChange(e)
                  setFormError('')
                }}
                onBlur={handleBlur}
                fullWidth
              />
              <div className={classes.formFooter}>
                {/* <div className={classes.checkboxWrapper}>
                  <Checkbox checked />
                  <Typography variant="caption">Remember me</Typography>
                </div> */}
                {
                  <Link to="/reset-password">
                    <Typography
                      color="primary"
                      className={classes.resetPasswordLink}
                    >
                      Need help with your password?
                    </Typography>
                  </Link>
                }
              </div>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                customColor="primary"
                isLoading={loading}
              >
                Sign in
              </Button>
              {formError && (
                <Typography className="l-errorMessage">{formError}</Typography>
              )}
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default LoginForm
